import { IMAGES, SITE as SITE_IT } from "./site";

export const SITE = {
  ...SITE_IT,
  tagline: "B&B in an 18th-century villa, ten minutes from Mantua",
  bookingUrl: "https://ledolcidimore.beddy.io?lang=en",
};

export { IMAGES };

export const NAV_LINKS = [
  { to: "/la-struttura", label: "The villa" },
  { to: "/camere", label: "Rooms" },
  { to: "/servizi-e-informazioni", label: "Services" },
  { to: "/il-territorio", label: "The area" },
  { to: "/guida", label: "Guide" },
  { to: "/faq", label: "FAQ" },
  { to: "/contatti", label: "Contact" },
];

const roomServicesBase = [
  "Kettle and coffee machine",
  "Mini-fridge with water, juice and yogurt",
  "Breakfast products in the room",
];

export const ROOMS = [
  {
    slug: "camera-101",
    name: "Room 101",
    type: "Standard double room",
    capacity: 2,
    beds: "1 double bed",
    idealFor: "Couples and solo travellers",
    image: "/images/camera-101-standard-le-dolci-dimore.jpg",
    imageAlt: "Room 101 at Le dolci Dimore: double bed with botanical prints and parquet floor",
    gallery: [
      "/images/camera-101-standard-le-dolci-dimore.jpg",
      "/images/camera-101-dettaglio-letto-le-dolci-dimore.jpg",
      "/images/bagno-camera-101-le-dolci-dimore.jpg",
    ],
    excerpt:
      "A bright, essential double room, designed for anyone looking for a well-kept base a stone's throw from Mantua.",
    description: [
      "Room 101 is a welcoming double on the first floor of the villa. The spaces are measured and tidy: a double bed, parquet flooring, botanical prints above the headboard and a private bathroom with shower, reserved for the room.",
      "It is the simplest choice for a couple visiting Mantua and its surroundings, but it is also available for single occupancy for those travelling alone, for leisure or for work.",
      "In the room you will find everything you need to start the day at your own pace: a kettle, a coffee machine and breakfast products, with water, juice and yogurt in the mini-fridge.",
    ],
    services: ["Private bathroom with shower", ...roomServicesBase],
  },
  {
    slug: "camera-102",
    name: "Room 102",
    type: "Superior room",
    capacity: 3,
    beds: "1 double bed + single sofa bed",
    idealFor: "Couples with a child or small groups of three",
    image: "/images/camera-102-superior-le-dolci-dimore.jpg",
    imageAlt: "Room 102 at Le dolci Dimore: double bed, side table and breakfast station",
    gallery: [
      "/images/camera-102-divano-letto-le-dolci-dimore.jpg",
      "/images/camera-102-zona-giorno-le-dolci-dimore.jpg",
      "/images/bagno-camera-102-le-dolci-dimore.jpg",
    ],
    excerpt:
      "More space and a single sofa bed: the right solution for travelling in three without giving up comfort.",
    description: [
      "Room 102 adds a single click-clack sofa bed to the double bed, bringing capacity to three guests. It is the natural choice for a couple with a child or for three adults sharing the trip.",
      "You can feel the extra space: a round table with chairs for breakfast in the room, a desk and the station with kettle and coffee machine. The private bathroom has a shower.",
      "The room is on the first floor of the villa and looks out onto the quiet countryside of Borgo Virgilio.",
    ],
    services: [
      "Private bathroom with shower",
      "Table and chairs for breakfast in the room",
      ...roomServicesBase,
    ],
  },
  {
    slug: "camera-103",
    name: "Room 103",
    type: "Deluxe room",
    capacity: 4,
    beds: "1 double bed + double sofa bed",
    idealFor: "Families and groups of up to four",
    image: "/images/camera-103-deluxe-le-dolci-dimore.jpg",
    imageAlt: "Room 103 at Le dolci Dimore: double bed, sofa bed and breakfast table",
    gallery: [
      "/images/camera-103-deluxe-le-dolci-dimore.jpg",
      "/images/camera-103-divano-letto-le-dolci-dimore.jpg",
      "/images/bagno-camera-103-le-dolci-dimore.jpg",
    ],
    excerpt:
      "The largest room in the villa: a double bed plus a double sofa bed, for families and small groups of up to four guests.",
    description: [
      "Room 103 is the largest at Le dolci Dimore: alongside the double bed there is a double sofa bed, for a total of four beds. It is the answer for families with two children or two couples of friends who want to share the same room.",
      "The parquet floor, the round table with chairs for breakfast in the room and the warm lighting make it a comfortable space to live in, not just to sleep in. The spacious, tiled private bathroom has a double sink, a shower and a bathtub.",
      "On the first floor of the villa, with the breakfast kit in the room and the mini-fridge stocked with water, juice and yogurt.",
    ],
    services: [
      "Private bathroom with double sink, shower and bathtub",
      "Table and chairs for breakfast in the room",
      ...roomServicesBase,
    ],
  },
  {
    slug: "camera-104",
    name: "Room 104",
    type: "Standard double room",
    capacity: 2,
    beds: "1 double bed",
    idealFor: "Couples and solo travellers",
    image: "/images/camera-104-standard-le-dolci-dimore.jpg",
    imageAlt: "Room 104 at Le dolci Dimore: double bed and breakfast table",
    gallery: [
      "/images/camera-104-vista-le-dolci-dimore.jpg",
      "/images/camera-104-letto-le-dolci-dimore.jpg",
      "/images/bagno-camera-104-le-dolci-dimore.jpg",
    ],
    excerpt:
      "The second double room in the house: same care, same private bathroom, an intimate atmosphere for sleeping well in the quiet of the countryside.",
    description: [
      "Room 104 shares the same philosophy as Room 101: a tidy, quiet double room on the first floor, with a private bathroom with shower.",
      "It is designed for two people, with the option of single occupancy. The parquet floor, the round table with chairs for breakfast in the room and the warm lighting make it cosy and comfortable. In the evening, the countryside around the villa guarantees a silence that is rare for those used to sleeping in the city.",
      "As in every room at Le dolci Dimore, breakfast is self-organised: croissants, crispbread, jams and Nutella, a kettle and coffee machine, and a mini-fridge with water, juice and yogurt.",
    ],
    services: [
      "Private bathroom with shower",
      "Table and chairs for breakfast in the room",
      ...roomServicesBase,
    ],
  },
];

export const SERVICES = [
  {
    title: "Private bathroom in every room",
    text: "All four rooms have their own private bathroom: nothing is shared with other guests.",
    icon: "bath",
  },
  {
    title: "Garden",
    text: "A large garden surrounds the villa: space to read, breathe and stay outdoors in the quiet of the countryside.",
    icon: "trees",
  },
  {
    title: "Indoor common area",
    text: "A shared space inside the villa, available to guests for a moment of rest.",
    icon: "armchair",
  },
  {
    title: "Phone assistance",
    text: "For your arrival and throughout your stay, we remain reachable by phone.",
    icon: "phone",
  },
  {
    title: "Simple breakfast in your room",
    text: "Croissants, crispbread, cereals, biscuits, honey, jams and Nutella; water, juice and yogurt in the fridge. Kettle and coffee machine provided.",
    icon: "coffee",
  },
  {
    title: "Flexible assisted check-in",
    text: "In-person welcome whenever possible; otherwise, remote access arranged with phone assistance.",
    icon: "key-round",
  },
];

export const PRACTICAL_INFO = [
  { label: "Check-in", value: "from 3:00 pm to 8:00 pm" },
  { label: "Check-out", value: "by 10:00 am" },
  { label: "Breakfast", value: "in your room, at your own pace" },
  { label: "Housekeeping", value: "rooms refreshed daily by midday" },
  { label: "Access", value: "in person or remotely, with phone assistance" },
  { label: "Rooms location", value: "first floor of the villa" },
];

export const TERRITORY = [
  {
    id: "mantova",
    title: "Mantua and its historic centre",
    intro:
      "Mantua is a city of art enclosed between three lakes, a UNESCO World Heritage Site together with Sabbioneta. Its historic centre is best explored on foot, among cobbled squares, arcades and Renaissance palaces.",
    places: [
      {
        name: "Piazza Sordello and the Cathedral",
        why: "The medieval heart of the city: one of the largest and most evocative squares in Italy, home to the Cathedral, Palazzo Bonacolsi and the monumental entrance to Palazzo Ducale.",
        distance: "about 10 minutes by car",
        link: "https://www.turismo.mantova.it/",
        linkLabel: "Mantua Tourism",
      },
      {
        name: "Piazza delle Erbe and the Rotonda di San Lorenzo",
        why: "The market square, with its arcades, the Clock Tower and the Rotonda di San Lorenzo, the oldest church in Mantua, inspired by Romanesque architecture.",
        distance: "about 10 minutes by car",
        link: "https://www.turismo.mantova.it/",
        linkLabel: "Mantua Tourism",
      },
      {
        name: "Basilica di Sant'Andrea",
        why: "A Renaissance masterpiece by Leon Battista Alberti, guarding the relic of the Most Precious Vessels and frescoes linked to Mantegna.",
        distance: "about 10 minutes by car",
        link: "https://www.turismo.mantova.it/",
        linkLabel: "Mantua Tourism",
      },
    ],
  },
  {
    id: "gonzaga",
    title: "The Gonzaga: Palazzo Ducale and Palazzo Te",
    intro:
      "For three centuries the Gonzaga family made Mantua one of the capitals of the Renaissance. Their palaces tell this story better than any book.",
    places: [
      {
        name: "Palazzo Ducale and Castello di San Giorgio",
        why: "A city within a city: over 500 rooms, courtyards and gardens. Inside the Castello di San Giorgio you will find the Camera degli Sposi, Andrea Mantegna's masterpiece.",
        distance: "about 10 minutes by car",
        link: "https://www.palazzoducalemantova.it/",
        linkLabel: "Official website",
      },
      {
        name: "Palazzo Te",
        why: "The pleasure villa commissioned by Federico II Gonzaga and designed by Giulio Romano: the Room of the Giants and the Room of Cupid and Psyche are among the most famous Mannerist frescoes.",
        distance: "about 10 minutes by car",
        link: "https://www.palazzote.it/",
        linkLabel: "Official website",
      },
    ],
  },
  {
    id: "mincio",
    title: "The lakes of Mantua and the Mincio Park",
    intro:
      "Mantua rises on a peninsula between the lakes formed by the river Mincio. The Mincio Park protects a unique water landscape, famous for the summer blooming of the lotus flowers.",
    places: [
      {
        name: "The lakes and the Mantua skyline",
        why: "The city skyline seen from the water is one of the most beautiful sights in Lombardy, especially at sunset, from the San Giorgio bridge or from Sparafucile.",
        distance: "about 10-15 minutes by car",
        link: "https://www.turismo.mantova.it/",
        linkLabel: "Mantua Tourism",
      },
      {
        name: "Boat trips on the Mincio and the lotus flowers",
        why: "Between July and August, expanses of lotus flowers transform the waters of the Mincio. Motorboats depart from Mantua and Grazie for excursions into the reserve.",
        distance: "boarding about 15-20 minutes by car",
        link: "https://www.parcodelmincio.it/",
        linkLabel: "Mincio Park",
      },
    ],
  },
  {
    id: "natura",
    title: "Nature, walks and cycle paths",
    intro:
      "The plain around Mantua is crossed by safe, scenic cycle paths running along river embankments and canals.",
    places: [
      {
        name: "Mantua – Peschiera del Garda cycle path",
        why: "About 45 km of almost entirely protected track from Mantua's Lago Superiore up the Mincio to Peschiera, through countryside, villages and river views.",
        distance: "route access about 15 minutes by car",
        link: "https://www.parcodelmincio.it/",
        linkLabel: "Mincio Park",
      },
      {
        name: "Walks along the embankments",
        why: "Easy itineraries on foot or by bike along the Mincio and the waterways of the lowlands, suitable for everyone, including children.",
        distance: "in the surroundings of the property",
        link: "https://www.parcodelmincio.it/",
        linkLabel: "Mincio Park",
      },
    ],
  },
  {
    id: "borghi",
    title: "Villages and towns of the province",
    intro:
      "The province of Mantua is dotted with villages worth discovering slowly, alternating them with visits to the city.",
    places: [
      {
        name: "Grazie di Curtatone",
        why: "A small village on the Mincio with the Sanctuary of the Blessed Virgin, famous for its street painters (madonnari) and the Assumption Fair on 15 August.",
        distance: "about 15-20 minutes by car",
        link: "https://www.turismo.mantova.it/",
        linkLabel: "Mantua Tourism",
      },
      {
        name: "Sabbioneta",
        why: "The Renaissance “ideal city” founded by Vespasiano Gonzaga: city walls, the Teatro all'Antica and Palazzo Giardino. UNESCO World Heritage together with Mantua.",
        distance: "about 35-40 minutes by car",
        link: "https://www.turismo.mantova.it/",
        linkLabel: "Mantua Tourism",
      },
      {
        name: "San Benedetto Po",
        why: "Home to the great Benedictine abbey of Polirone, one of the most important monastic complexes in Italy, linked to Matilda of Canossa.",
        distance: "about 25-30 minutes by car",
        link: "https://www.turismo.mantova.it/",
        linkLabel: "Mantua Tourism",
      },
      {
        name: "Castellaro Lagusello and Borghetto sul Mincio",
        why: "Two of Italy's most photographed villages: the first overlooking a morainic lake, the second among the mills and Visconti bridges of Valeggio.",
        distance: "about 40-45 minutes by car",
        link: "https://www.turismo.mantova.it/",
        linkLabel: "Mantua Tourism",
      },
    ],
  },
  {
    id: "cucina",
    title: "Mantuan cuisine and local products",
    intro:
      "The Mantuan table is a reason to travel in itself: a cuisine of court and countryside, made of stuffed pasta, rice, cured meats and ancient desserts.",
    places: [
      {
        name: "Tortelli di zucca",
        why: "The signature dish: fresh pasta filled with pumpkin, mostarda and amaretti, served with melted butter and sage. To be tried at least once.",
        distance: "trattorias and restaurants a few minutes from the property",
        link: "https://www.turismo.mantova.it/",
        linkLabel: "Mantua Tourism",
      },
      {
        name: "Risotto alla pilota, sbrisolona and Mantuan salami",
        why: "Rice “alla pilota” with puntel, the sbrisolona cake broken by hand and the soft salami of the lowlands: the trilogy of rural tradition.",
        distance: "trattorias and restaurants a few minutes from the property",
        link: "https://www.turismo.mantova.it/",
        linkLabel: "Mantua Tourism",
      },
    ],
  },
  {
    id: "eventi",
    title: "Events and seasonal appointments",
    intro:
      "The area lives on annual appointments that are worth a stay on their own.",
    places: [
      {
        name: "Festivaletteratura",
        why: "In September, for five days, Mantua hosts one of Europe's most important literary festivals: talks, readings and performances in every square and courtyard.",
        distance: "Mantua city centre, about 10 minutes by car",
        link: "https://www.festivaletteratura.it/",
        linkLabel: "Official website",
      },
      {
        name: "The lotus flower bloom",
        why: "Between July and August, lotus flowers colour the waters of the Mincio: a natural spectacle unique in Italy, best seen early in the morning or at sunset.",
        distance: "Grazie di Curtatone area, about 15-20 minutes by car",
        link: "https://www.parcodelmincio.it/",
        linkLabel: "Mincio Park",
      },
      {
        name: "Assumption Fair in Grazie",
        why: "Every 15 August the churchyard of the Sanctuary of Grazie fills with coloured chalks: madonnari from all over the world draw through the night.",
        distance: "about 15-20 minutes by car",
        link: "https://www.turismo.mantova.it/",
        linkLabel: "Mantua Tourism",
      },
    ],
  },
  {
    id: "dintorni",
    title: "Day trips nearby",
    intro:
      "Le dolci Dimore is also a convenient base for exploring cities and landscapes beyond the province.",
    places: [
      {
        name: "Verona",
        why: "The Arena, Juliet's balcony, the squares and the Adige river: one of the most beautiful cities in Northern Italy, perfect for a day trip.",
        distance: "about 45 minutes by car",
        link: "https://www.visitverona.it/",
        linkLabel: "Official website",
      },
      {
        name: "Lake Garda",
        why: "Peschiera, Sirmione and the southern shores of Garda are less than an hour away: lakeside walks, villages and wineries in the morainic hills.",
        distance: "about 45 minutes by car",
        link: "https://www.visitgarda.com/",
        linkLabel: "Visit Garda",
      },
    ],
  },
];

export const ARTICLES = [
  {
    slug: "cosa-vedere-a-mantova-in-un-giorno",
    title: "What to see in Mantua in one day",
    excerpt:
      "Twenty-four hours in the city of the Gonzaga: a walking itinerary between Palazzo Ducale, the squares of the centre and sunset over the lakes.",
    image: IMAGES.mantova,
    date: "July 2026",
    readTime: "6 min",
    content: [
      {
        type: "p",
        text: "Mantua is easy to visit in one day because its historic centre is compact and almost entirely pedestrian. The ideal base is outside the traffic: sleeping in Borgo Virgilio, at Le dolci Dimore, you reach the centre in about ten minutes by car and return to the quiet of the countryside in the evening.",
      },
      { type: "h2", text: "Morning: Palazzo Ducale and the Camera degli Sposi" },
      {
        type: "p",
        text: "Start from Piazza Sordello, the medieval heart of the city. Dedicate the morning to Palazzo Ducale, the Gonzaga palace: an enormous complex of courtyards, frescoed halls and hanging gardens. The unmissable stop is the Camera degli Sposi in the Castello di San Giorgio, painted by Andrea Mantegna: it is worth booking entry in advance on the official website, as access is limited.",
      },
      { type: "h2", text: "Afternoon: squares, arcades and Sant'Andrea" },
      {
        type: "p",
        text: "After lunch in a trattoria in the centre — the perfect occasion for tortelli di zucca — stroll towards Piazza delle Erbe, with the Clock Tower and the Rotonda di San Lorenzo, the oldest church in the city. From there, a few steps lead to the Basilica of Sant'Andrea, designed by Leon Battista Alberti: its single-nave interior is one of the most imposing sacred spaces of the Renaissance.",
      },
      { type: "h2", text: "If time allows: Palazzo Te" },
      {
        type: "p",
        text: "If the day permits, Palazzo Te deserves the detour: the villa of Federico II Gonzaga, frescoed by Giulio Romano, with the famous Room of the Giants. From the centre it is about a fifteen-minute walk or a few minutes by car.",
      },
      { type: "h2", text: "Evening: sunset over the lakes" },
      {
        type: "p",
        text: "End the day on the shores of Lago di Mezzo or from the San Giorgio bridge: Mantua's skyline reflected on the water at sunset is the image you will take home. Then back to Le dolci Dimore, just minutes away, far from parking and noise.",
      },
    ],
  },
  {
    slug: "weekend-a-mantova-itinerario",
    title: "What to do in Mantua on a weekend",
    excerpt:
      "Two days between art, nature and good food: the historic centre on Saturday, the Mincio and the villages on Sunday.",
    image: IMAGES.palazzoDucale,
    date: "July 2026",
    readTime: "7 min",
    content: [
      {
        type: "p",
        text: "A weekend is the perfect format for Mantua: one day for the city and one for the surrounding area. Staying at Le dolci Dimore you have a quiet base in Borgo Virgilio, with the centre about ten minutes away and the roads of the lowlands starting almost from the gate.",
      },
      { type: "h2", text: "Saturday: the city of the Gonzaga" },
      {
        type: "p",
        text: "Dedicate the first day to the historic centre. Morning at Palazzo Ducale and Mantegna's Camera degli Sposi (book your entry), lunch in the centre, afternoon between Piazza delle Erbe, the Rotonda di San Lorenzo and the Basilica of Sant'Andrea. If you are in the mood for a second museum, choose Palazzo Te: the Room of the Giants alone is worth the visit.",
      },
      { type: "h2", text: "Sunday: the Mincio and the villages" },
      {
        type: "p",
        text: "On the second day, explore the water landscape of the Mincio Park. Between July and August the lotus flower bloom is a spectacle not to be missed, also visible from the motorboats departing from Mantua and Grazie di Curtatone. Alternatively, rent a bike and ride a stretch of the Mantua–Peschiera cycle path, protected and almost entirely flat.",
      },
      { type: "h2", text: "Stops in the surroundings" },
      {
        type: "p",
        text: "If you prefer villages to water, Grazie di Curtatone with its sanctuary is a quarter of an hour away; Sabbioneta, the Renaissance “ideal city” and UNESCO World Heritage Site, just over half an hour. On the way back, stop for dinner at a trattoria in the lowlands: risotto alla pilota and Mantuan salami are the right way to end the weekend.",
      },
      { type: "h2", text: "Where to sleep for a weekend in Mantua" },
      {
        type: "p",
        text: "For a weekend you need a base that is easy to reach, convenient for the car and quiet at night. Le dolci Dimore, on Strada Romana in Borgo Virgilio, combines all three: four rooms with private bathroom in an 18th-century villa, a few minutes from the Mantova Sud motorway exit and from the centre.",
      },
    ],
  },
  {
    slug: "natura-e-percorsi-lungo-il-mincio",
    title: "Nature and trails along the Mincio",
    excerpt:
      "Lotus flowers, embankment cycle paths and river navigation: how to experience the Mincio Park starting from Borgo Virgilio.",
    image: IMAGES.mincio,
    date: "July 2026",
    readTime: "6 min",
    content: [
      {
        type: "p",
        text: "The Mincio is the thread of water that links Mantua to Lake Garda: first it forms the three lakes that embrace the city, then it flows slowly south between embankments and reserves. The Mincio Park protects this landscape and is one of the best reasons to stay more than one night in the area.",
      },
      { type: "h2", text: "The lotus flower bloom" },
      {
        type: "p",
        text: "Between July and August, the waters of Lago Superiore and the river are covered with lotus flowers, a plant that arrived from Asia a century ago and has become Mantua's summer symbol. The most beautiful viewing points are along the banks between Mantua and Grazie di Curtatone: the Park's motorboats allow you to cross the flowering expanses up close.",
      },
      { type: "h2", text: "The Mantua – Peschiera cycle path" },
      {
        type: "p",
        text: "It is one of Italy's most loved river cycle paths: about 45 kilometres almost entirely on protected track, from Mantua's Lago Superiore to Peschiera del Garda, following the river. You don't need to ride it all: even a stretch of a few kilometres offers mills, bridges and glimpses of the river.",
      },
      { type: "h2", text: "On foot, slowly" },
      {
        type: "p",
        text: "Those who prefer walking will find easy routes on the embankments and in the Park's reserves, such as the wetland area around Grazie. Early morning and late afternoon are the moments with the best light and the most birdlife: herons, black-winged stilts and, with a bit of luck, the kingfisher.",
      },
      { type: "h2", text: "Planning the excursion" },
      {
        type: "p",
        text: "From Le dolci Dimore the Park entrances are about fifteen minutes by car. Bring water and a hat in summer, check motorboat times and availability on the Mincio Park website and, if you want to cycle, ask us for advice on the nearest rental: we will gladly point you to the updated options.",
      },
    ],
  },
  {
    slug: "cosa-mangiare-a-mantova",
    title: "What to eat in Mantua: a guide to Mantuan cuisine",
    excerpt:
      "Tortelli di zucca, risotto alla pilota, sbrisolona: the dishes to try and the courtly and rural history they tell.",
    image: IMAGES.food,
    date: "July 2026",
    readTime: "6 min",
    content: [
      {
        type: "p",
        text: "Mantuan cuisine has two souls: the refined one of the Gonzaga court and the hearty one of the countryside. The result is a table full of dishes you won't find elsewhere, which alone justify the trip.",
      },
      { type: "h2", text: "First courses: tortelli and risotto" },
      {
        type: "p",
        text: "Tortello di zucca is the signature dish: pasta filled with pumpkin, Mantuan mostarda and amaretti, dressed with butter and sage. Its counterpart is risotto alla pilota, born among the piloti who hulled rice in the mills: Vialone Nano rice finished with puntel, that is, with luganega sausage.",
      },
      { type: "h2", text: "Cured meats and cheeses" },
      {
        type: "p",
        text: "Mantuan salami is soft, scented with garlic and wine, to be cut by hand into thick slices. With it come ciccioli, cotechino and Grana Padano from the plain. Agnoli in broth is the festive dish, but in winter it is often found on weekday menus too.",
      },
      { type: "h2", text: "Desserts: sbrisolona and anello di Monaco" },
      {
        type: "p",
        text: "The sbrisolona cake, hard and rich in almonds, is not cut: it is broken by hand, as tradition dictates. The anello di Monaco is instead a leavened dessert of Swiss-Italian tradition, glazed and soft, linked to the history of immigrant pastry-making communities.",
      },
      { type: "h2", text: "Where to taste" },
      {
        type: "p",
        text: "Trattorias and restaurants serving traditional cuisine can be found both in the centre of Mantua and in the countryside of Borgo Virgilio and its surroundings, often a few minutes from the property. On arrival we will gladly point you to updated addresses based on the season and opening days.",
      },
    ],
  },
];

export const FAQS = [
  {
    q: "Where is Le dolci Dimore located?",
    a: "At Strada Romana 69/A in Borgo Virgilio, in the province of Mantua, surrounded by the Mantuan countryside and a few minutes from the A22 Mantova Sud motorway exit.",
  },
  {
    q: "How far is it from the centre of Mantua?",
    a: "Mantua's historic centre is about 6-8 km away, roughly ten minutes by car. Exact distances depend on your destination in the city.",
  },
  {
    q: "What room types are available?",
    a: "Four rooms, all with private bathroom: 101 and 104 are Standard doubles (up to 2 guests), 102 is the Superior with a double bed and a single sofa bed (up to 3), and 103 is the Deluxe with a double bed and a double sofa bed (up to 4).",
  },
  {
    q: "How many people can stay in each room?",
    a: "The Standard rooms host up to 2 people (also for single use), the Superior up to 3, the Deluxe up to 4.",
  },
  {
    q: "Do the rooms have a private bathroom?",
    a: "Yes, all four rooms have their own private bathroom.",
  },
  {
    q: "What time is check-in?",
    a: "Check-in is from 3:00 pm to 8:00 pm. If you have particular schedule needs, contact us in advance: we will work out the best solution together.",
  },
  {
    q: "By what time must the room be vacated?",
    a: "Check-out is by 10:00 am, to allow housekeeping, which is completed by midday.",
  },
  {
    q: "How does remote access work?",
    a: "When we cannot welcome you in person, we arrange remote access: you receive instructions before arrival and we remain available by phone to assist you at every step.",
  },
  {
    q: "How is breakfast organised?",
    a: "Independently, directly in your room: you will find croissants, crispbread, cereals, biscuits, honey, jams and Nutella, with a kettle and coffee machine. Water, juice and yogurt are available in the fridge.",
  },
  {
    q: "How can I book directly?",
    a: "Through the “Book” button on every page of the site, which leads to our official Beddy booking engine, where you can check up-to-date availability and rates. Alternatively you can call or write to us.",
  },
  {
    q: "How can I contact the property?",
    a: "By phone at +39 0376 1587002, by email at info@ledolcidimore.it or via the form on the Contact page.",
  },
  {
    q: "Is the property suitable for business travellers?",
    a: "Yes: the location near the A22 Mantova Sud exit, the quiet of the countryside and the self-organised breakfast in the room make it convenient for business stays.",
  },
];
